import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { DocumentPageResponse } from './types';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import { VersionType } from './enums';

const getDocumentTypeMetadata: QueryFunction<
  DocumentPageResponse,
  ['/document-meta-data', string]
> = async ({ queryKey }) => {
  if (isMockServices()) return await mock(_mockData);
  const response = await getDocumentPages(queryKey[1]);

  return response;
};

export default getDocumentTypeMetadata;

const _mockData: DocumentPageResponse = {
  success: true,
  pages: [
    {
      id: 'page1',
      imageUrl: '/mocks/image-02.webp?id=${documentId}',
      thumbnailUrl: '/mocks/image-02.webp?id=${documentId}',
      height: 3300,
      width: 2550,
      dpix: null,
      dpiy: null,
      classification: 'Closing Disclosure',
      classificationConfidence: 97,
      number: 1,
      pdfUrl: '/mocks/image-02.pdf?id=${documentId}',
      pageId: 'page1',
      pageOrder: 1,
    },
    {
      id: 'page2',
      imageUrl: '/mocks/image-02.webp?id=${documentId}',
      thumbnailUrl: '/mocks/image-02.webp?id=${documentId}',
      height: 3300,
      width: 2550,
      dpix: null,
      dpiy: null,
      classification: 'Closing Disclosure',
      classificationConfidence: 97,
      number: 2,
      pdfUrl: '/mocks/image-02.pdf?id=${documentId}',
      pageId: 'page2',
      pageOrder: 2,
    },
  ],
  document: {
    id: 'document1',
    name: 'Document 1',
    documentTypeId: 'document-type-1',
    confidence: 95,
    version: 0,
    versionIdentifier: VersionType.FINAL,
    pages: [],
    fields: [],
    documentType: {
      id: 'document-type-1',
      name: 'Document Type 1',
      displayName: 'Document Type 1',
      fields: [],
      filenamePrefix: null,
      classificationReviewThreshold: 0,
      extractionReviewThreshold: 0,
      borrowerReviewThreshold: 0,
      highConfThresh: 0,
    },
    storageUrl: '/mocks/document-01.pdf?id=${documentId}',
    metadata: {
      external_document_filename: 'Document 1.pdf',
    },
    isNew: false,
    isVerified: true,
    isViewed: true,
    userApproved: true,
    userTasks: [],
  },
};

export const getDocumentPages = async (
  documentId: string,
  includeDeletedDocument: boolean = false,
): Promise<DocumentPageResponse> => {
  if (isMockServices()) return _mockData;
  const url = includeDeletedDocument
    ? `/documents/${documentId}/pages?includeDeleted=true`
    : `/documents/${documentId}/pages`;
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<DocumentPageResponse>(url, {
    method: 'GET',
  });

  if (response.data) {
    const pageOrderMap = new Map(
      (response.data.document.documentPages ?? []).map((documentPage) => [
        documentPage.pageId,
        documentPage.pageOrder,
      ]),
    );

    return {
      success: true,
      pages: response.data.pages
        .map((page) => ({
          ...page,
          pageOrder: pageOrderMap.get(page.id) ?? page.pageOrder ?? page.number,
        }))
        .sort((a, b) => (a.pageOrder ?? a.number) - (b.pageOrder ?? b.number)),
      document: response.data.document,
    };
  }
  throw new Error('Failed to fetch document pages');
};
